import "core-js/modules/es.array.map.js";
import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.object.to-string.js";
/**
 * 传播大脑融媒客户端 JS-SDK 工具模块
/**
 * 文档: https://open.commind.com/base/documents/11897473974536
 * 
 * 使用说明:
 * 1. 页面已自动引入 SDK 脚本 (public/index.html)
 * 2. 通过 window.CP2.bridgeType !== undefined 判断是否在客户端内
 * 3. 客户端主线版本需 ≥ v6.7.0，低版本调用返回错误码 11000
 * 4. 调试: URL 添加参数 sdkDebug=1 (Eruda) 或 sdkDebug=2 (vConsole)
 */

// SDK 错误码常量
var ERROR_CODES = {
  FAIL: 0,
  // 调用失败
  SUCCESS: 1,
  // 调用成功
  NO_MAP_CLIENT: 10042,
  // 没有地图客户端
  VERSION_NOT_SUPPORT: 11000,
  // 当前版本不支持
  METHOD_NOT_EXIST: 11001,
  // API方法不存在
  PARAM_INVALID: 11002,
  // API参数不合法
  PARAM_NOT_EXIST: 11003,
  // 指定参数不存在
  USER_CANCEL: 12001,
  // 用户取消操作
  USER_NOT_LOGIN: 12002,
  // 用户未登录
  SERVER_ERROR: 13001,
  // 服务器连接失败
  SERVER_TIMEOUT: 13002,
  // 服务器超时
  NO_PERMISSION: 13003,
  // 服务器白名单返回无权限
  CALL_LIMIT: 13004 // 调用次数超限
};

// 最低支持版本
var MIN_SUPPORT_VERSION = '6.7.0';

/**
 * 检查 SDK 是否可用
 * @returns {boolean}
 */
export function isSdkAvailable() {
  return typeof window.CP2 !== 'undefined' && window.CP2.bridgeType !== undefined;
}

/**
 * 获取客户端信息
 * @returns {{ type: string, version: string } | null}
 * @returns {string} type - 客户端类型: 'xsb'(学习包) | 'wx'(微信) | ''(其他)
 * @returns {string} version - 客户端版本号
 */
export function getAppInfo() {
  if (!isSdkAvailable()) {
    return null;
  }
  return window.CP2.appInfo || null;
}

/**
 * 获取客户端类型
 * @returns {string | null} 'xsb' | 'wx' | '' | null
 */
export function getAppType() {
  var info = getAppInfo();
  return info ? info.type : null;
}

/**
 * 获取客户端版本号
 * @returns {string | null}
 */
export function getAppVersion() {
  var info = getAppInfo();
  return info ? info.version : null;
}

/**
 * 比较客户端版本是否满足最低要求
 * @param {string} [minVersion='6.7.0'] - 最低版本要求
 * @returns {boolean}
 */
export function isVersionSupported(minVersion) {
  minVersion = minVersion || MIN_SUPPORT_VERSION;
  var currentVersion = getAppVersion();
  if (!currentVersion) return false;
  var current = currentVersion.split('.').map(Number);
  var required = minVersion.split('.').map(Number);
  for (var i = 0; i < Math.max(current.length, required.length); i++) {
    var cur = current[i] || 0;
    var req = required[i] || 0;
    if (cur > req) return true;
    if (cur < req) return false;
  }
  return true;
}

/**
 * 通用 SDK API 调用封装
 * @param {string} methodName - API 方法名
 * @param {Object} [params={}] - 调用参数
 * @returns {Promise<any>} 返回 SDK 回调结果
 */
export function callSdkApi(methodName) {
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return new Promise(function (resolve, reject) {
    // 检查 SDK 是否可用
    if (!isSdkAvailable()) {
      reject({
        code: -1,
        message: '当前不在传播大脑融媒客户端内，SDK不可用'
      });
      return;
    }

    // 检查版本是否支持
    if (!isVersionSupported()) {
      reject({
        code: ERROR_CODES.VERSION_NOT_SUPPORT,
        message: "\u5F53\u524D\u7248\u672C\u4E0D\u652F\u6301\uFF0C\u8BF7\u5347\u7EA7\u81F3 ".concat(MIN_SUPPORT_VERSION, " \u53CA\u4EE5\u4E0A")
      });
      return;
    }

    // 检查方法是否存在
    if (typeof window.CP2[methodName] !== 'function') {
      reject({
        code: ERROR_CODES.METHOD_NOT_EXIST,
        message: "API \u65B9\u6CD5 ".concat(methodName, " \u4E0D\u5B58\u5728")
      });
      return;
    }

    // 调用 SDK 方法
    try {
      window.CP2[methodName](params, function (result) {
        if (result && (result.code === ERROR_CODES.SUCCESS || result.code === 1)) {
          resolve(result);
        } else {
          reject(result || {
            code: ERROR_CODES.FAIL,
            message: '调用失败'
          });
        }
      });
    } catch (error) {
      reject({
        code: ERROR_CODES.FAIL,
        message: error.message || 'SDK 调用异常'
      });
    }
  });
}
export { ERROR_CODES, MIN_SUPPORT_VERSION };

/**
 * 获取当前位置信息（使用高德定位服务）
 * window.CP2.getLocation() 返回 Promise，无需参数
 * 
 * @returns {Promise<{lng: string, lat: string, address: string, city: string, province: string, region: string}>}
 */
export function getLocation() {
  return new Promise(function (resolve, reject) {
    if (!isSdkAvailable()) {
      reject({
        code: -1,
        message: 'SDK不可用'
      });
      return;
    }
    // getLocation 返回 Promise，直接调用
    window.CP2.getLocation().then(function (res) {
      if (res && res.code == 1 && res.data) {
        // 注意：经度字段文档拼写为 lontitude
        resolve({
          lng: res.data.lontitude,
          lat: res.data.latitude,
          address: res.data.address,
          city: res.data.city,
          province: res.data.province,
          region: res.data.region
        });
      } else {
        reject(res || {
          code: ERROR_CODES.FAIL,
          message: '定位失败'
        });
      }
    }).catch(function (err) {
      reject(err || {
        code: ERROR_CODES.FAIL,
        message: '定位异常'
      });
    });
  });
}